import {useEffect, useState} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import PageLayout from '../layout/PageLayout';
import { GamerService, UserData } from "../../services/GamerService"
import { AdminService } from "../../services/AdminService"
import { FlashInvadersService } from "../../services/FlashInvadersService"
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import './ProfilePage.css';

const ADMIN_USER_ID = "google-oauth2|104974972507269250508";

// UUID Flash Invaders format: 8-4-4-4-12 hex chars (uppercase)
const UUID_REGEX = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;

const isValidFlashInvadersUuid = (uuid: string): boolean => {
    return UUID_REGEX.test(uuid);
};

const ProfilePage = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const [syncResult, setSyncResult] = useState<string | null>(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isEditingUuid, setIsEditingUuid] = useState(false);
    const [uuidInput, setUuidInput] = useState("");
    const [uuidSaving, setUuidSaving] = useState(false);
    const [uuidError, setUuidError] = useState<string | null>(null);
    const [canSyncClient, setCanSyncClient] = useState(false);
    const [isClientSyncing, setIsClientSyncing] = useState(false);
    const [clientSyncResult, setClientSyncResult] = useState<string | null>(null);
    const [clientSyncError, setClientSyncError] = useState<string | null>(null);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const isAdmin = user?.sub === ADMIN_USER_ID;

    useEffect(() => {
        setIsLoadingUser(true);
        setLoadError(null);
        GamerService.saveGamer(getAccessTokenSilently, user?.sub, user?.given_name, user?.family_name)
            .then((data) => {
                setUserData(data);
                setUuidInput(data.uuid || "");
            })
            .catch((error) => {
                console.error("Erreur lors de la sauvegarde de l'utilisateur :", error);
                setLoadError(error instanceof Error ? error.message : "Erreur de chargement");
            })
            .finally(() => {
                setIsLoadingUser(false);
            });
    }, [getAccessTokenSilently, user]);

    // Vérifier si l'utilisateur peut synchroniser
    useEffect(() => {
        if (userData?.uuid) {
            GamerService.canSync(getAccessTokenSilently)
                .then(setCanSyncClient)
                .catch((error) => console.error("Erreur lors de la vérification de sync :", error));
        }
    }, [getAccessTokenSilently, userData?.uuid]);

    const handleEditUuid = () => {
        setIsEditingUuid(true);
        setUuidError(null);
    };

    const handleCancelEdit = () => {
        setIsEditingUuid(false);
        setUuidInput(userData?.uuid || "");
        setUuidError(null);
    };

    const handleSaveUuid = async () => {
        setUuidError(null);

        // Valider le format de l'UUID
        const trimmedUuid = uuidInput.trim().toUpperCase();
        if (trimmedUuid && !isValidFlashInvadersUuid(trimmedUuid)) {
            setUuidError("Format invalide. Exemple: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX");
            return;
        }

        setUuidSaving(true);
        try {
            const updatedUser = await GamerService.updateUuid(getAccessTokenSilently, trimmedUuid);
            setUserData(updatedUser);
            setUuidInput(trimmedUuid);
            setIsEditingUuid(false);
        } catch (err) {
            setUuidError(err instanceof Error ? err.message : "Erreur inconnue");
        } finally {
            setUuidSaving(false);
        }
    };

    const handleSync = async () => {
        setIsSyncing(true);
        setSyncError(null);
        setSyncResult(null);

        try {
            const result = await AdminService.triggerSync(getAccessTokenSilently);
            setSyncResult(result);
        } catch (err) {
            setSyncError(err instanceof Error ? err.message : "Erreur inconnue");
        } finally {
            setIsSyncing(false);
        }
    };

    const handleClientSync = async () => {
        if (!userData?.uuid) return;

        setIsClientSyncing(true);
        setClientSyncError(null);
        setClientSyncResult(null);

        try {
            // 1. Appeler l'API Flash Invaders directement depuis le navigateur
            const flashData = await FlashInvadersService.fetchGallery(userData.uuid);

            // 2. Envoyer les données au backend
            const result = await GamerService.syncFromClient(getAccessTokenSilently, flashData);

            if (result.success) {
                setClientSyncResult("Synchronisation terminee !");
                setCanSyncClient(false);
                // Recharger les données utilisateur
                const updatedUser = await GamerService.getMe(getAccessTokenSilently);
                setUserData(updatedUser);
            } else {
                setClientSyncError(result.message);
            }
        } catch (err) {
            setClientSyncError(err instanceof Error ? err.message : "Erreur inconnue");
        } finally {
            setIsClientSyncing(false);
        }
    };

    if (!user) {
        return null;
    }

    return (
        <PageLayout>
            <div className="content-layout">
                <div className="profile-terminal">
                    <div className="profile-terminal__header">
                        <div className="profile-terminal__dots">
                            <span className="profile-terminal__dot profile-terminal__dot--red"></span>
                            <span className="profile-terminal__dot profile-terminal__dot--yellow"></span>
                            <span className="profile-terminal__dot profile-terminal__dot--green"></span>
                        </div>
                        <span className="profile-terminal__title">~/profile</span>
                    </div>

                    <div className="profile-terminal__body">
                        {/* Profile Header */}
                        <div className="profile-header">
                            <img
                                src={user.picture}
                                alt="Profile"
                                className="profile-header__avatar"
                            />
                            <div className="profile-header__info">
                                <h2 className="profile-header__name">{user.name}</h2>
                                <span className="profile-header__email">{user.email}</span>
                            </div>
                        </div>

                        {/* UUID Section */}
                        <div className="profile-section">
                            <div className="profile-section__header">
                                <span className="profile-section__icon">🔑</span>
                                <h3 className="profile-section__title">UUID Flash Invaders</h3>
                            </div>
                            <p className="profile-section__subtitle">
                                Votre identifiant unique pour synchroniser avec l'app officielle
                            </p>

                            {loadError && (
                                <div className="alert alert--error" style={{ marginBottom: '12px' }}>
                                    <ErrorIcon className="alert__icon" />
                                    Erreur de chargement: {loadError}
                                </div>
                            )}

                            {isLoadingUser ? (
                                <div className="uuid-display">
                                    <span className="uuid-value uuid-value--empty">Chargement...</span>
                                </div>
                            ) : !isEditingUuid ? (
                                <div className="uuid-display">
                                    <span className={`uuid-value ${!userData?.uuid ? 'uuid-value--empty' : ''}`}>
                                        {userData?.uuid || "Non défini"}
                                    </span>
                                    <button className="btn btn--outline" onClick={handleEditUuid}>
                                        <EditIcon className="btn__icon" />
                                        Modifier
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <div className="uuid-display">
                                        <input
                                            type="text"
                                            className="uuid-input"
                                            value={uuidInput}
                                            onChange={(e) => setUuidInput(e.target.value)}
                                            placeholder="Entrez votre UUID"
                                            disabled={uuidSaving}
                                        />
                                        <button
                                            className={`btn btn--success ${uuidSaving ? 'btn--disabled' : ''}`}
                                            onClick={handleSaveUuid}
                                            disabled={uuidSaving}
                                        >
                                            <SaveIcon className="btn__icon" />
                                            {uuidSaving ? 'Enregistrement...' : 'Enregistrer'}
                                        </button>
                                        <button
                                            className="btn btn--outline-danger"
                                            onClick={handleCancelEdit}
                                            disabled={uuidSaving}
                                        >
                                            <CancelIcon className="btn__icon" />
                                            Annuler
                                        </button>
                                    </div>
                                    {uuidError && (
                                        <div className="alert alert--error">
                                            <ErrorIcon className="alert__icon" />
                                            Erreur: {uuidError}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Flash Invaders Profile */}
                        <div className="profile-section flash-profile">
                            <div className="profile-section__header">
                                <span className="profile-section__icon">👾</span>
                                <h3 className="profile-section__title">Profil Flash Invaders</h3>
                            </div>

                            {userData?.flashProfile ? (
                                <>
                                    <div className="flash-profile__stats">
                                        <div className="flash-profile__stat">
                                            <div className="flash-profile__stat-label">Pseudo</div>
                                            <div className="flash-profile__stat-value flash-profile__stat-value--pseudo">
                                                {userData.flashProfile.pseudo}
                                            </div>
                                        </div>
                                        <div className="flash-profile__stat">
                                            <div className="flash-profile__stat-label">Score</div>
                                            <div className="flash-profile__stat-value flash-profile__stat-value--score">
                                                {userData.flashProfile.score.toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="flash-profile__stat">
                                            <div className="flash-profile__stat-label">Rang mondial</div>
                                            <div className="flash-profile__stat-value flash-profile__stat-value--rank">
                                                #{userData.flashProfile.rank.toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="flash-profile__stat">
                                            <div className="flash-profile__stat-label">Invaders</div>
                                            <div className="flash-profile__stat-value flash-profile__stat-value--invaders">
                                                {userData.flashProfile.siFound}
                                            </div>
                                        </div>
                                        <div className="flash-profile__stat">
                                            <div className="flash-profile__stat-label">Villes</div>
                                            <div className="flash-profile__stat-value flash-profile__stat-value--cities">
                                                {userData.flashProfile.cityFound}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flash-profile__sync-info">
                                        <div className="flash-profile__sync-date">
                                            Derniere sync : {new Date(userData.flashProfile.lastSyncDate).toLocaleString('fr-FR')}
                                        </div>
                                        <button
                                            className={`btn btn--primary ${(!canSyncClient || isClientSyncing) ? 'btn--disabled' : ''}`}
                                            onClick={handleClientSync}
                                            disabled={!canSyncClient || isClientSyncing}
                                            title={!canSyncClient ? "Synchronisation possible une fois par semaine" : ""}
                                        >
                                            <SyncIcon className={`btn__icon ${isClientSyncing ? 'btn__icon--spin' : ''}`} />
                                            {isClientSyncing ? 'Sync...' : 'Synchroniser'}
                                        </button>
                                    </div>
                                    {clientSyncResult && (
                                        <div className="alert alert--success">
                                            <CheckCircleIcon className="alert__icon" />
                                            {clientSyncResult}
                                        </div>
                                    )}
                                    {clientSyncError && (
                                        <div className="alert alert--error">
                                            <ErrorIcon className="alert__icon" />
                                            {clientSyncError}
                                        </div>
                                    )}
                                </>
                            ) : userData?.uuid ? (
                                <div className="flash-profile__placeholder">
                                    <div className="flash-profile__placeholder-icon">⏳</div>
                                    <h4 className="flash-profile__placeholder-title">Premiere synchronisation</h4>
                                    <p className="flash-profile__placeholder-text">
                                        Cliquez sur le bouton ci-dessous pour synchroniser vos donnees Flash Invaders.
                                    </p>
                                    <button
                                        className={`btn btn--primary ${isClientSyncing ? 'btn--disabled' : ''}`}
                                        onClick={handleClientSync}
                                        disabled={isClientSyncing}
                                    >
                                        <SyncIcon className={`btn__icon ${isClientSyncing ? 'btn__icon--spin' : ''}`} />
                                        {isClientSyncing ? 'Synchronisation...' : 'Synchroniser maintenant'}
                                    </button>
                                    {clientSyncResult && (
                                        <div className="alert alert--success">
                                            <CheckCircleIcon className="alert__icon" />
                                            {clientSyncResult}
                                        </div>
                                    )}
                                    {clientSyncError && (
                                        <div className="alert alert--error">
                                            <ErrorIcon className="alert__icon" />
                                            {clientSyncError}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flash-profile__placeholder">
                                    <div className="flash-profile__placeholder-icon">🔗</div>
                                    <h4 className="flash-profile__placeholder-title">Connectez votre compte</h4>
                                    <p className="flash-profile__placeholder-text">
                                        Renseignez votre UUID ci-dessus pour synchroniser vos donnees.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Admin Section */}
                        {isAdmin && (
                            <div className="profile-section admin-section">
                                <div className="profile-section__header">
                                    <AdminPanelSettingsIcon style={{ color: 'var(--primary)', fontSize: '1.3rem' }} />
                                    <h3 className="profile-section__title">Administration</h3>
                                </div>

                                <p className="profile-section__subtitle">
                                    Synchroniser les invaders depuis pnote.eu
                                </p>

                                <button
                                    className={`btn btn--cyan ${isSyncing ? 'btn--disabled' : ''}`}
                                    onClick={handleSync}
                                    disabled={isSyncing}
                                >
                                    <SyncIcon className={`btn__icon ${isSyncing ? 'btn__icon--spin' : ''}`} />
                                    {isSyncing ? 'Synchronisation en cours...' : 'Sync Invaders (pnote.eu)'}
                                </button>

                                {syncResult && (
                                    <div className="alert alert--success">
                                        <CheckCircleIcon className="alert__icon" />
                                        {syncResult}
                                    </div>
                                )}

                                {syncError && (
                                    <div className="alert alert--error">
                                        <ErrorIcon className="alert__icon" />
                                        Erreur: {syncError}
                                    </div>
                                )}

                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default ProfilePage;